import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { User } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import DashboardGuardModal from './DashboardGuardModal';

const Navbar = () => {
  const { loginWithRedirect, logout, isAuthenticated, isLoading } = useAuth0();
  const navigate = useNavigate();

  const [showDashboardGuard, setShowDashboardGuard] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleGoToDashboard = () => {
    if (!isAuthenticated && !isLoading) {
      setShowDashboardGuard(true);
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-tax-borderGray bg-white shadow-sm">
      <div className="container flex h-16 items-center justify-between">
        <div className="mr-4 flex items-center">
          <Link to="/" className="flex items-center space-x-2">
            <img
              src="/lovable-uploads/f53ef72a-5796-421d-8697-737a8a9dbbb5.png"
              alt="Property Tax Hero Logo"
              className="h-12 w-auto"
            />
          </Link>
        </div>

        {/* Hamburger button for small screens */}
        <button
          className="lg:hidden flex items-center text-tax-neutralGray focus:outline-none"
          onClick={() => setIsMobileMenuOpen((prev) => !prev)}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            {isMobileMenuOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>

        {/* Desktop nav */}
        <nav className="hidden flex-1 lg:flex items-center justify-end space-x-4">
          <Link
            to="/about"
            className="text-base font-medium text-tax-neutralGray transition-colors hover:text-tax-deepBlue"
          >
            About Us
          </Link>
          <Link
            to="/agents"
            className="text-base font-medium text-tax-neutralGray transition-colors hover:text-tax-deepBlue"
          >
            For Agents
          </Link>
          <Link
            to="/faq"
            className="text-base font-medium text-tax-neutralGray transition-colors hover:text-tax-deepBlue"
          >
            FAQ
          </Link>
          {!isAuthenticated && !isLoading ? (
            <Button
              variant="outline"
              className="hidden sm:flex border-tax-borderGray hover:bg-tax-lightGray text-tax-neutralGray hover:text-tax-deepBlue rounded-lg"
              onClick={() =>
                loginWithRedirect({
                  appState: {
                    returnTo: '/dashboard?tab=reports',
                  },
                })
              }
            >
              Login
            </Button>
          ) : (
            <Button
              variant="outline"
              className="hidden sm:flex border-tax-borderGray hover:bg-tax-lightGray text-tax-neutralGray hover:text-tax-deepBlue rounded-lg"
              onClick={() => {
                window.localStorage.removeItem('_token');
                logout({
                  logoutParams: {
                    returnTo: window.location.origin,
                  },
                });
              }}
            >
              Logout
            </Button>
          )}
          <Button
            asChild
            variant="outline"
            className="hidden sm:flex border-tax-borderGray hover:bg-tax-lightGray text-tax-neutralGray hover:text-tax-deepBlue rounded-lg"
          >
            <button onClick={handleGoToDashboard}>
              <User className="w-4 h-4 mr-2" />
              My Dashboard
            </button>
          </Button>
          <Button
            asChild
            className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg"
          >
            <Link
              to="/#property-form"
              onClick={(e) => {
                if (window.location.pathname === '/') {
                  e.preventDefault();
                  const propertyForm = document.getElementById('property-form-section');
                  if (propertyForm) {
                    propertyForm.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    });

                    setTimeout(() => {
                      const addressInput = document.getElementById('address');
                      if (addressInput) addressInput.focus();
                    }, 500);
                  }
                }
              }}
            >
              Get Started
            </Link>
          </Button>
        </nav>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden px-4 pb-4 space-y-4 flex flex-col items-center">
          <Link
            to="/about"
            className="block text-tax-neutralGray hover:text-tax-deepBlue"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            About Us
          </Link>
          <Link
            to="/agents"
            className="block text-tax-neutralGray hover:text-tax-deepBlue"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            For Agents
          </Link>
          <Link
            to="/faq"
            className="block text-tax-neutralGray hover:text-tax-deepBlue"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            FAQ
          </Link>
          {!isAuthenticated ? (
            <Button
              variant="outline"
              className="max-w-xs mx-auto min-h-[3rem] px-6 border-tax-borderGray hover:bg-tax-lightGray text-tax-neutralGray hover:text-tax-deepBlue rounded-lg"
              onClick={() => {
                setIsMobileMenuOpen(false);
                loginWithRedirect({
                  appState: {
                    returnTo: `${window.location.origin}/dashboard?tab=reports`,
                  },
                });
              }}
            >
              Login
            </Button>
          ) : (
            <Button
              variant="outline"
              className="max-w-xs mx-auto min-h-[3rem] px-6 border-tax-borderGray hover:bg-tax-lightGray text-tax-neutralGray hover:text-tax-deepBlue rounded-lg"
              onClick={() => {
                setIsMobileMenuOpen(false);
                window.localStorage.removeItem('_token');
                logout({
                  logoutParams: {
                    returnTo: window.location.origin,
                  },
                });
              }}
            >
              Logout
            </Button>
          )}
          <Button
            variant="outline"
            className="max-w-xs mx-auto min-h-[3rem] px-6 border-tax-borderGray hover:bg-tax-lightGray text-tax-neutralGray hover:text-tax-deepBlue rounded-lg"
            onClick={() => {
              setIsMobileMenuOpen(false);
              handleGoToDashboard();
            }}
          >
            <User className="w-4 h-4 mr-2" />
            My Dashboard
          </Button>
          <Button
            asChild
            className="max-w-xs mx-auto min-h-[3rem] px-6 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg"
          >
            <Link
              to="/#property-form"
              onClick={(e) => {
                setIsMobileMenuOpen(false);
                if (window.location.pathname === '/') {
                  e.preventDefault();
                  const propertyForm = document.getElementById('property-form-section');
                  if (propertyForm) {
                    propertyForm.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    });

                    setTimeout(() => {
                      const addressInput = document.getElementById('address');
                      if (addressInput) addressInput.focus();
                    }, 500);
                  }
                }
              }}
            >
              Get Started
            </Link>
          </Button>
        </div>
      )}

      <DashboardGuardModal
        show={showDashboardGuard}
        onClose={() => setShowDashboardGuard(false)}
      />
    </header>
  );
};

export default Navbar;
